import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, MapPin, Zap, Play, Trash2,
  Map, Database, ChevronLeft, ChevronRight, ExternalLink,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LazyGoogleMap from '../../components/map/LazyGoogleMap';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminParcelCounty = () => {
  const { countyId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Auto-heal
  const [healing, setHealing] = useState(false);
  const [healProgress, setHealProgress] = useState(null);

  // Parcels browser
  const [parcelsPage, setParcelsPage] = useState({ page: 1, total: 0, total_pages: 0, parcels: [] });
  const [parcelsLoading, setParcelsLoading] = useState(false);

  // Test-on-Map
  const [showMap, setShowMap] = useState(false);

  // Fetch
  const [fetchingCounty, setFetchingCounty] = useState(false);

  // Preview image (cached)
  const [previewBust, setPreviewBust] = useState(Date.now()); // cache-buster on regen
  const [regenPreview, setRegenPreview] = useState(false);
  const [regenProgress, setRegenProgress] = useState(null);

  // Migrate to dedicated collection
  const [migrating, setMigrating] = useState(false);
  const [migrateProgress, setMigrateProgress] = useState(null);

  // Test query result
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/county/${countyId}`, {
      headers: authHeaders(),
    });
    if (res.ok && res.data) {
      setDetail(res.data);
      setError(null);
    } else {
      setError(res.error || `HTTP ${res.status}`);
    }
    setLoading(false);
  }, [countyId]);

  const loadParcels = useCallback(async (page = 1) => {
    setParcelsLoading(true);
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/county/${countyId}/parcels?page=${page}&limit=50`, {
      headers: authHeaders(),
    });
    if (res.ok && res.data) {
      setParcelsPage(res.data);
    }
    setParcelsLoading(false);
  }, [countyId]);

  useEffect(() => {
    loadDetail();
    loadParcels(1);
  }, [loadDetail, loadParcels]);

  // Auto-trigger Auto-Heal once when the backend reports count_status timed_out
  // (production cold-start scenario: county_id index isn't built yet, count
  // queries time out, ALL counters show 0, and the user-facing UI is useless).
  // Running Auto-Heal builds the indexes and unblocks every subsequent query.
  // Guarded by `_autoHealedKey` in sessionStorage so we don't loop on refresh.
  useEffect(() => {
    if (!detail) return;
    if (healing) return;
    const needsHeal = detail.count_status && detail.count_status !== 'ok';
    if (!needsHeal) return;
    const guardKey = `parcel_auto_heal_triggered_${countyId}`;
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, String(Date.now()));
    setMessage({
      type: 'info',
      text: `Count queries timed out — running Auto-Heal automatically to rebuild indexes. This page will refresh once it finishes.`,
    });
    handleAutoHeal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.count_status, countyId]);

  // County Auto-Heal — `skipConfirm=true` is used by the auto-trigger on mount
  // when count_status is timed_out (no user confirmation needed in that path).
  const handleAutoHeal = async (skipConfirm = false) => {
    if (!skipConfirm && !window.confirm(`Run Auto-Heal for ${detail?.county_name || countyId}?\n\nSteps:\n  1. Ensure 2dsphere index on location\n  2. Backfill location field for this county\n  3. Validate with $geoWithin test\n\nRuns in background.`)) {
      return;
    }
    setHealing(true);
    setHealProgress({ status: 'starting', progress: 0, step: 'starting', message: 'Starting...' });

    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/county/${countyId}/auto-heal`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      setMessage({ type: 'error', text: `Auto-Heal failed to start: ${res.error}` });
      setHealing(false);
      setHealProgress(null);
      return;
    }
    const processId = res.data?.process_id;
    if (!processId) {
      setMessage({ type: 'error', text: 'Auto-Heal did not return a process_id' });
      setHealing(false);
      setHealProgress(null);
      return;
    }
    setHealProgress({ status: 'running', progress: 0, step: 'starting', message: res.data.message });

    let attempts = 0;
    const maxAttempts = 600;
    const poll = async () => {
      attempts++;
      const sRes = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/repair-geo-data/status/${processId}`, {
        headers: authHeaders(),
      });
      if (sRes.ok && sRes.data) {
        setHealProgress(sRes.data);
        if (sRes.data.status === 'completed') {
          setMessage({ type: sRes.data.validation_ok ? 'success' : 'info', text: sRes.data.message || 'Auto-Heal complete' });
          setHealing(false);
          setTimeout(() => { setHealProgress(null); loadDetail(); }, 2500);
          return;
        }
        if (sRes.data.status === 'failed') {
          setMessage({ type: 'error', text: sRes.data.message || 'Auto-Heal failed' });
          setHealing(false);
          return;
        }
      }
      if (attempts >= maxAttempts) {
        setMessage({ type: 'error', text: 'Auto-Heal polling timed out' });
        setHealing(false);
        return;
      }
      setTimeout(poll, 3000);
    };
    setTimeout(poll, 3000);
  };

  // County Re-fetch from upstream
  const handleFetch = async () => {
    if (!window.confirm(`Auto-fetch ${detail?.county_name || countyId}? This will download parcels from the county API.`)) return;
    setFetchingCounty(true);
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/auto-fetch`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ county: countyId }),
    });
    if (res.ok && res.data) {
      setMessage({ type: 'info', text: res.data.message || 'Fetch started in background' });
    } else {
      setMessage({ type: 'error', text: `Fetch failed: ${res.error}` });
    }
    setFetchingCounty(false);
    setTimeout(loadDetail, 2000);
  };

  // County delete
  const handleDelete = async () => {
    if (!window.confirm(`PERMANENTLY DELETE all ${detail?.parcel_count?.toLocaleString() || ''} parcels for ${detail?.county_name}?\n\nThis cannot be undone.`)) return;
    if (!window.confirm('Are you absolutely sure? Click OK to confirm permanent deletion.')) return;
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/clear/${countyId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setMessage({ type: 'success', text: 'County data deleted' });
      setTimeout(() => navigate('/admin/parcel-data'), 1000);
    } else {
      setMessage({ type: 'error', text: `Delete failed: ${res.error}` });
    }
  };

  // Background poller helper — used by regen-preview and migrate
  const pollProcess = async (processId, setProgress, onDone) => {
    let attempts = 0;
    const maxAttempts = 600;
    const tick = async () => {
      attempts++;
      const sRes = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/repair-geo-data/status/${processId}`, {
        headers: authHeaders(),
      });
      if (sRes.ok && sRes.data) {
        setProgress(sRes.data);
        if (sRes.data.status === 'completed' || sRes.data.status === 'failed') {
          onDone(sRes.data);
          return;
        }
      }
      if (attempts < maxAttempts) {
        setTimeout(tick, 2500);
      } else {
        onDone({ status: 'failed', message: 'Polling timed out' });
      }
    };
    setTimeout(tick, 1500);
  };

  // Regenerate cached map preview PNG
  const handleRegeneratePreview = async () => {
    setRegenPreview(true);
    setRegenProgress({ status: 'starting', progress: 0, message: 'Starting...' });
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/county/${countyId}/regenerate-preview`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok || !res.data?.process_id) {
      setMessage({ type: 'error', text: `Preview regen failed to start: ${res.error}` });
      setRegenPreview(false);
      setRegenProgress(null);
      return;
    }
    pollProcess(res.data.process_id, setRegenProgress, (final) => {
      setMessage({ type: final.status === 'completed' ? 'success' : 'error', text: final.message || 'Preview regenerated' });
      setRegenPreview(false);
      setTimeout(() => { setRegenProgress(null); setPreviewBust(Date.now()); loadDetail(); }, 1000);
    });
  };

  // Migrate this county to its own dedicated MongoDB collection
  const handleMigrateToDedicated = async () => {
    if (!window.confirm(`Migrate ${detail?.county_name || countyId} to its own dedicated collection (parcels_${countyId})?\n\nThis is a one-time operation that creates a separate MongoDB collection for this county. The global usa_parcels collection is left untouched for zero-downtime fallback. Idempotent — safe to re-run.`)) {
      return;
    }
    setMigrating(true);
    setMigrateProgress({ status: 'starting', progress: 0, message: 'Starting migration...' });
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/county/${countyId}/migrate-to-dedicated`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok || !res.data?.process_id) {
      setMessage({ type: 'error', text: `Migration failed to start: ${res.error}` });
      setMigrating(false);
      setMigrateProgress(null);
      return;
    }
    pollProcess(res.data.process_id, setMigrateProgress, (final) => {
      setMessage({ type: final.status === 'completed' ? 'success' : 'error', text: final.message || 'Migration done' });
      setMigrating(false);
      setTimeout(() => { setMigrateProgress(null); loadDetail(); }, 1500);
    });
  };

  // Real $geoWithin test query
  const handleTestQuery = async () => {
    setTesting(true);
    setTestResult(null);
    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/county/${countyId}/test-query`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    });
    if (res.ok && res.data) {
      setTestResult(res.data);
      setMessage({ type: res.data.ok ? 'success' : 'info', text: res.data.message });
    } else {
      setMessage({ type: 'error', text: `Test failed: ${res.error}` });
    }
    setTesting(false);
  };

  if (loading && !detail) {
    return (
      <div className="p-8 flex items-center justify-center" data-testid="county-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="county-error">
        <Button variant="ghost" onClick={() => navigate('/admin/parcel-data')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
          <p className="font-semibold text-rose-900">Failed to load county detail</p>
          <p className="text-sm text-rose-700 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-parcel-county-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin/parcel-data')} className="text-gray-600" data-testid="back-to-parcel-data-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> All Counties
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="county-name">{detail.county_name}</h1>
            <p className="text-sm text-gray-500">
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{detail.county_id}</code>
              {detail.state ? ` · ${detail.state}` : ''}
              {detail.city ? ` · ${detail.city}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {detail.has_2dsphere_index ? (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">2dsphere ✓</Badge>
          ) : (
            <Badge className="bg-rose-100 text-rose-800 border border-rose-200">2dsphere missing</Badge>
          )}
          {detail.health_ok ? (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Healthy</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Needs attention</Badge>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`rounded-lg p-3 text-sm ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' :
          message.type === 'error' ? 'bg-rose-50 text-rose-900 border border-rose-200' :
          'bg-blue-50 text-blue-900 border border-blue-200'
        }`} data-testid="county-message">
          {message.text}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white" data-testid="kpi-parcel-count">
          <p className="text-blue-100 text-xs uppercase tracking-wide">Parcels</p>
          <p className="text-3xl font-bold mt-1">{detail.parcel_count.toLocaleString()}</p>
          {detail.coverage_percent != null && (
            <p className="text-blue-200 text-xs mt-1">{detail.coverage_percent}% of expected</p>
          )}
        </div>
        <div className={`rounded-xl p-5 text-white ${detail.parcels_with_location === detail.parcel_count ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-amber-500 to-amber-600'}`} data-testid="kpi-with-location">
          <p className="text-white/80 text-xs uppercase tracking-wide">With location</p>
          <p className="text-3xl font-bold mt-1">{detail.parcels_with_location.toLocaleString()}</p>
        </div>
        <div className={`rounded-xl p-5 text-white ${detail.parcels_missing_location > 0 ? 'bg-gradient-to-br from-rose-500 to-rose-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`} data-testid="kpi-missing-location">
          <p className="text-white/80 text-xs uppercase tracking-wide">Missing location</p>
          <p className="text-3xl font-bold mt-1">{detail.parcels_missing_location.toLocaleString()}</p>
        </div>
        <div className={`rounded-xl p-5 text-white ${detail.has_2dsphere_index ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-rose-500 to-rose-600'}`} data-testid="kpi-index">
          <p className="text-white/80 text-xs uppercase tracking-wide">2dsphere index</p>
          <p className="text-3xl font-bold mt-1">{detail.has_2dsphere_index ? 'Present' : 'Missing'}</p>
        </div>
      </div>

      {/* Count-status warning (production: when count_documents timed out) */}
      {detail.count_status && detail.count_status !== 'ok' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="county-count-status-warning">
          <RefreshCw className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 text-sm">Count queries timed out</p>
            <p className="text-xs text-amber-700 mt-0.5">
              The <code>count_documents</code> query took longer than 8 seconds, likely because the <code>county_id</code> index isn&apos;t built yet. Numbers above may be 0 even though parcels exist. <strong>Click &quot;Auto-Heal This County&quot; below</strong> — step 1 creates the index and the next refresh will show correct counts in milliseconds.
            </p>
          </div>
        </div>
      )}

      {/* Coverage Progress Bar */}
      {detail.expected_total > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-coverage-bar">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 text-sm">Coverage</h3>
            <span className="text-sm font-bold tabular-nums">
              {detail.coverage_percent != null ? `${detail.coverage_percent}%` : '—'}
              <span className="text-gray-400 text-xs ml-2 font-normal">
                {detail.parcel_count.toLocaleString()} / {detail.expected_total.toLocaleString()}
              </span>
            </span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ${
                (detail.coverage_percent || 0) >= 90 ? 'bg-emerald-500' :
                (detail.coverage_percent || 0) >= 50 ? 'bg-blue-500' :
                (detail.coverage_percent || 0) > 0 ? 'bg-amber-500' : 'bg-gray-300'
              }`}
              style={{ width: `${Math.min(detail.coverage_percent || 0, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Cached Map Preview Image */}
      {detail.parcel_count > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-preview-image-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Map className="w-4 h-4 text-blue-600" /> Cached Map Preview
              </h3>
              {detail.preview_meta?.generated_at && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Generated {new Date(detail.preview_meta.generated_at).toLocaleString()} · {(detail.preview_meta.dot_count || 0).toLocaleString()} dots sampled from {(detail.preview_meta.total_parcels || 0).toLocaleString()} parcels
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRegeneratePreview}
                disabled={regenPreview}
                size="sm"
                variant="outline"
                className="text-sm"
                data-testid="county-regen-preview-btn"
              >
                {regenPreview ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                {detail.preview_meta ? 'Regenerate' : 'Generate'} Preview
              </Button>
              {detail.bbox && (
                <Button onClick={() => setShowMap(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="county-open-interactive-map-btn">
                  <Map className="w-4 h-4 mr-1" /> Open Interactive Map
                </Button>
              )}
            </div>
          </div>
          {detail.preview_meta ? (
            <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <img
                src={`${API}/api/admin/usa-parcel-data/county/${detail.county_id}/preview.png?t=${previewBust}&token=${encodeURIComponent(localStorage.getItem('token') || localStorage.getItem('admin_token') || '')}`}
                alt={`Parcel coverage preview for ${detail.county_name}`}
                className="w-full h-auto cursor-pointer"
                onClick={() => setShowMap(true)}
                data-testid="county-preview-image"
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500">
              <Map className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No preview generated yet. Click <strong>Generate Preview</strong> above.</p>
            </div>
          )}
          {regenPreview && regenProgress && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="county-regen-progress">
              <div className="flex items-center justify-between mb-2 text-xs">
                <span className="font-medium text-blue-900">{regenProgress.message || 'Generating...'}</span>
                <span className="font-bold">{regenProgress.progress || 0}%</span>
              </div>
              <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${regenProgress.progress || 0}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dedicated Collection Banner */}
      {detail.parcel_count > 0 && (
        <div
          className={`rounded-xl p-4 border flex items-center justify-between ${detail.using_dedicated_collection ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}
          data-testid="county-dedicated-banner"
        >
          <div className="flex items-center gap-3">
            <Database className={`w-5 h-5 ${detail.using_dedicated_collection ? 'text-emerald-600' : 'text-blue-600'}`} />
            <div>
              <p className={`font-semibold text-sm ${detail.using_dedicated_collection ? 'text-emerald-900' : 'text-blue-900'}`}>
                {detail.using_dedicated_collection
                  ? `Using dedicated collection: ${detail.dedicated_collection_name}`
                  : 'Using shared usa_parcels collection (not yet migrated)'}
              </p>
              <p className={`text-xs mt-0.5 ${detail.using_dedicated_collection ? 'text-emerald-700' : 'text-blue-700'}`}>
                {detail.using_dedicated_collection
                  ? 'Queries for this county now run against its own collection — faster, more isolated.'
                  : 'Migrate to a dedicated MongoDB collection for this county for faster queries and easier maintenance. Zero downtime; idempotent.'}
              </p>
            </div>
          </div>
          {!detail.using_dedicated_collection && (
            <Button
              onClick={handleMigrateToDedicated}
              disabled={migrating}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm whitespace-nowrap"
              data-testid="county-migrate-btn"
            >
              {migrating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Migrate to Dedicated
            </Button>
          )}
        </div>
      )}
      {migrating && migrateProgress && (
        <div className="bg-white border-2 border-blue-200 rounded-xl p-4" data-testid="county-migrate-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-blue-900">{migrateProgress.message || 'Migrating...'}</span>
            <span className="text-sm font-bold text-blue-900 tabular-nums">{migrateProgress.progress || 0}%</span>
          </div>
          <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${migrateProgress.progress || 0}%` }} />
          </div>
        </div>
      )}

      {/* Actions row */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-wrap gap-2">
        <Button
          onClick={() => handleAutoHeal(false)}
          disabled={healing || (detail.parcel_count === 0 && (!detail.count_status || detail.count_status === 'ok'))}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="county-auto-heal-btn"
        >
          {healing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          Auto-Heal This County
        </Button>
        {detail.has_api && (
          <Button onClick={handleFetch} disabled={fetchingCounty} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="county-fetch-btn">
            {fetchingCounty ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {detail.parcel_count > 0 ? 'Re-fetch' : 'Fetch'} Parcels
          </Button>
        )}
        <Button
          onClick={handleTestQuery}
          disabled={testing || detail.parcel_count === 0}
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="county-test-query-btn"
        >
          {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          Test ($geoWithin)
        </Button>
        {detail.bbox && (
          <Button variant="outline" onClick={() => setShowMap(true)} data-testid="county-test-map-btn">
            <Map className="w-4 h-4 mr-2" /> Show on Map
          </Button>
        )}
        <Button variant="outline" onClick={loadDetail} data-testid="county-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
        {detail.parcel_count > 0 && (
          <Button variant="ghost" onClick={handleDelete} className="text-rose-600 hover:bg-rose-50 ml-auto" data-testid="county-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Delete All Parcels
          </Button>
        )}
      </div>

      {/* Test result panel */}
      {testResult && (
        <div
          className={`rounded-xl p-4 border ${testResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
          data-testid="county-test-result"
        >
          <p className={`font-semibold text-sm ${testResult.ok ? 'text-emerald-900' : 'text-rose-900'}`}>
            {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Source collection: <code className="bg-white/60 px-1 rounded">{testResult.source}</code>
          </p>
          {testResult.sample && testResult.sample.length > 0 && (
            <details className="mt-2 text-xs text-gray-700">
              <summary className="cursor-pointer">Sample parcels returned ({testResult.sample.length})</summary>
              <ul className="mt-1 space-y-1">
                {testResult.sample.map((s, i) => (
                  <li key={i}>
                    {s.full_address || `${s.house_number || ''} ${s.street_name || ''}`.trim() || '(no address)'} — ({s.latitude}, {s.longitude})
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Heal progress */}
      {healing && healProgress && (
        <div className="bg-white border-2 border-orange-200 rounded-xl p-5 shadow-sm" data-testid="county-heal-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              <h3 className="font-semibold text-gray-900">Auto-Heal running</h3>
            </div>
            <span className="text-sm font-bold text-orange-700 tabular-nums">{Math.min(healProgress.progress || 0, 100)}%</span>
          </div>
          <div className="w-full h-3 bg-orange-100 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500"
              style={{ width: `${Math.min(healProgress.progress || 0, 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-700 font-medium">{healProgress.message || 'Working...'}</p>
          {Array.isArray(healProgress.steps) && healProgress.steps.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs">
              {healProgress.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`mt-0.5 ${
                    s.status === 'ok' ? 'text-emerald-600' :
                    s.status === 'error' ? 'text-rose-600' :
                    s.status === 'warning' ? 'text-amber-600' :
                    'text-blue-500'
                  }`}>
                    {s.status === 'ok' ? '✓' : s.status === 'error' ? '✗' : s.status === 'warning' ? '⚠' : '•'}
                  </span>
                  <span className="text-gray-700"><strong>{s.name}</strong> — {s.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Sample + bbox info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-bbox-panel">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" /> Bounding Box
          </h3>
          {detail.bbox ? (
            <div className="text-xs font-mono space-y-1 text-gray-700">
              <p>SW: {detail.bbox.min_lat.toFixed(5)}, {detail.bbox.min_lng.toFixed(5)}</p>
              <p>NE: {detail.bbox.max_lat.toFixed(5)}, {detail.bbox.max_lng.toFixed(5)}</p>
              <p>Center: {detail.center?.lat.toFixed(5)}, {detail.center?.lng.toFixed(5)}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No bounding box (no parcels imported yet).</p>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-sample-panel">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-600" /> Sample Parcel
          </h3>
          {detail.sample ? (
            <div className="text-xs space-y-1 text-gray-700">
              <p><strong>Address:</strong> {detail.sample.full_address || `${detail.sample.house_number || ''} ${detail.sample.street_name || ''}`.trim() || '(no address)'}</p>
              <p><strong>Coords:</strong> {detail.sample.latitude}, {detail.sample.longitude}</p>
              <p><strong>Has location:</strong> {detail.sample.location ? '✓' : '✗'}</p>
              <p><strong>Type:</strong> {detail.sample.property_type || '(none)'}</p>
              {detail.sample.fetched_at && (
                <p><strong>Fetched:</strong> {new Date(detail.sample.fetched_at).toLocaleString()}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No sample available.</p>
          )}
        </div>
      </div>

      {/* Last fetch log */}
      {detail.last_fetch && (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-last-fetch">
          <h3 className="font-semibold text-gray-900 mb-3">Last Fetch</h3>
          <div className="text-xs grid grid-cols-2 md:grid-cols-4 gap-3 text-gray-700">
            <div><p className="text-gray-500">When</p><p>{new Date(detail.last_fetch.timestamp).toLocaleString()}</p></div>
            <div><p className="text-gray-500">Fetched</p><p>{(detail.last_fetch.fetched_count || 0).toLocaleString()}</p></div>
            <div><p className="text-gray-500">Errors</p><p>{detail.last_fetch.error_count || 0}</p></div>
            <div><p className="text-gray-500">Type</p><p>{detail.last_fetch.import_type || 'auto'}</p></div>
          </div>
        </div>
      )}

      {/* Parcels paginator */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="county-parcels-table">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Browse Parcels</h3>
          <p className="text-xs text-gray-500">
            Page {parcelsPage.page} of {parcelsPage.total_pages.toLocaleString()} · {parcelsPage.total.toLocaleString()} parcels
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Address</th>
                <th className="text-left px-3 py-2 font-semibold">Street</th>
                <th className="text-right px-3 py-2 font-semibold">Lat</th>
                <th className="text-right px-3 py-2 font-semibold">Lng</th>
                <th className="text-center px-3 py-2 font-semibold">Loc</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {parcelsLoading ? (
                <tr><td colSpan="6" className="text-center py-6 text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline" /></td></tr>
              ) : parcelsPage.parcels.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-6 text-gray-400">No parcels.</td></tr>
              ) : parcelsPage.parcels.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-700">{p.full_address || p.house_number || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{p.street_name || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{p.lat?.toFixed(5)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{p.lng?.toFixed(5)}</td>
                  <td className="px-3 py-2 text-center">{p.has_location ? '✓' : '✗'}</td>
                  <td className="px-3 py-2 text-gray-500">{p.property_type || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3">
          <Button
            variant="outline"
            size="sm"
            disabled={parcelsPage.page <= 1 || parcelsLoading}
            onClick={() => loadParcels(parcelsPage.page - 1)}
            data-testid="parcels-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={parcelsPage.page >= parcelsPage.total_pages || parcelsLoading}
            onClick={() => loadParcels(parcelsPage.page + 1)}
            data-testid="parcels-next-btn"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Source / Download URL */}
      {detail.download_url && (
        <a
          href={detail.download_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
        >
          <ExternalLink className="w-4 h-4 mr-1" />
          Open original county data source
        </a>
      )}

      {/* Test-on-Map modal */}
      {showMap && detail.bbox && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMap(false)} data-testid="county-map-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="county-map-modal">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="font-bold text-gray-900">{detail.county_name} — Bounding Box</h3>
              <button onClick={() => setShowMap(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <div className="min-h-[500px]">
              <LazyGoogleMap
                center={{ lat: detail.center.lat, lng: detail.center.lng }}
                zoom={10}
                style={{ height: '500px', width: '100%' }}
                polygon={[
                  [detail.bbox.min_lat, detail.bbox.min_lng],
                  [detail.bbox.min_lat, detail.bbox.max_lng],
                  [detail.bbox.max_lat, detail.bbox.max_lng],
                  [detail.bbox.max_lat, detail.bbox.min_lng],
                ]}
                polygonOptions={{ fillColor: '#3b82f6', fillOpacity: 0.12, strokeColor: '#3b82f6', strokeWeight: 2 }}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowMap(false)}>Close</Button>
              <Button
                onClick={() => {
                  sessionStorage.setItem('parcel_zoom_to', JSON.stringify({ center: detail.center, county_name: detail.county_name }));
                  navigate('/admin/create-job');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="county-open-create-job-btn"
              >
                <MapPin className="w-4 h-4 mr-1" /> Open in Create Job
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminParcelCounty;
